// First Created - 22 Oct 2026
// Author - Atul Pandey

import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'

import { apiSlice } from '@/app/api/apiSlice'


/*
|--------------------------------------------------------------------------
| 01 - Evaluation Dimensions Adapter
|--------------------------------------------------------------------------
*/

const evalDimensionsAdapter = createEntityAdapter({})

const initialState =
    evalDimensionsAdapter.getInitialState()


/*
|--------------------------------------------------------------------------
| 02 - Evaluation Dimensions API Slice
|--------------------------------------------------------------------------
*/

export const evalDimensionsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({

        /*
        |--------------------------------------------------------------------------
        | Get Evaluation Dimensions
        |--------------------------------------------------------------------------
        |
        | Optional filters:
        |
        | categoryId
        | active = true | false | all
        |
        */

        getEvalDimensions: builder.query({

            query: ({
                categoryId,
                active = true,
            } = {}) => ({

                url: '/evalDimensions/getEvalDimensions',

                params: {

                    ...(categoryId && {
                        categoryId
                    }),

                    active: String(active),

                },

                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },

            }),

            transformResponse: responseData => {

                const loadedEvalDimensions = responseData.map(
                    evalDimension => ({

                        ...evalDimension,

                        id: evalDimension._id,

                    })
                )

                return evalDimensionsAdapter.setAll(
                    initialState,
                    loadedEvalDimensions
                )

            },

            providesTags: (result) => {

                if (result?.ids) {

                    return [
                        {
                            type: 'EvalDimension',
                            id: 'LIST'
                        },

                        ...result.ids.map(
                            id => ({
                                type: 'EvalDimension',
                                id
                            })
                        )
                    ]

                }

                return [
                    {
                        type: 'EvalDimension',
                        id: 'LIST'
                    }
                ]

            },

        }),


        /*
        |--------------------------------------------------------------------------
        | Get Evaluation Dimension By ID
        |--------------------------------------------------------------------------
        */

        getEvalDimensionById: builder.query({

            query: ({ id }) => ({

                url: '/evalDimensions/getEvalDimensionById',

                params: {
                    id
                },

                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },

            }),

            providesTags: (result, error, arg) => [
                {
                    type: 'EvalDimension',
                    id: arg?.id
                }
            ],

        }),


        /*
        |--------------------------------------------------------------------------
        | Create New Evaluation Dimension
        |--------------------------------------------------------------------------
        |
        | Required:
        |   categoryId
        |   dimensionName
        |
        */

        createNewEvalDimension: builder.mutation({

            query: initialEvalDimension => ({

                url: '/evalDimensions/createNewEvalDimension',

                method: 'POST',

                body: {
                    ...initialEvalDimension
                },

            }),

            invalidatesTags: [
                {
                    type: 'EvalDimension',
                    id: 'LIST'
                }
            ],

        }),


        /*
        |--------------------------------------------------------------------------
        | Update Evaluation Dimension
        |--------------------------------------------------------------------------
        */

        updateEvalDimension: builder.mutation({

            query: initialEvalDimension => ({

                url: '/evalDimensions/updateEvalDimension',

                method: 'PUT',

                body: {
                    ...initialEvalDimension
                },

            }),

            invalidatesTags: (result, error, arg) => [
                {
                    type: 'EvalDimension',
                    id: 'LIST'
                },
                {
                    type: 'EvalDimension',
                    id: arg.id
                }
            ],

        }),


        /*
        |--------------------------------------------------------------------------
        | Update Evaluation Dimension Status
        |--------------------------------------------------------------------------
        */

        updateEvalDimensionStatus: builder.mutation({

            query: ({ id, active }) => ({

                url: '/evalDimensions/updateEvalDimensionStatus',

                method: 'PATCH',

                body: {
                    id,
                    active
                },

            }),

            invalidatesTags: (result, error, arg) => [
                {
                    type: 'EvalDimension',
                    id: 'LIST'
                },
                {
                    type: 'EvalDimension',
                    id: arg.id
                }
            ],

        }),

        /*
        |--------------------------------------------------------------------------
        | 29 Oct 2026 - Hard Delete Evaluation Dimension
        |--------------------------------------------------------------------------
        */

        deleteEvalDimension: builder.mutation({

            query: ({ id }) => ({

                url: '/evalDimensions/deleteEvalDimension',

                method: 'DELETE',

                body: {
                    id,
                },

            }),

            invalidatesTags: (result, error, arg) =>
                error
                    ? []
                    : [
                        {
                            type: 'EvalDimension',
                            id: 'LIST',
                        },
                        {
                            type: 'EvalDimension',
                            id: arg.id,
                        },
                    ],

        }),

    }),
})


/*
|--------------------------------------------------------------------------
| 03 - Export Hooks
|--------------------------------------------------------------------------
*/

export const {

    useGetEvalDimensionsQuery,
    useLazyGetEvalDimensionsQuery,

    useGetEvalDimensionByIdQuery,

    useCreateNewEvalDimensionMutation,
    useUpdateEvalDimensionMutation,
    useUpdateEvalDimensionStatusMutation,

    useDeleteEvalDimensionMutation,         // 29 Oct 2026

    

} = evalDimensionsApiSlice


/*
|--------------------------------------------------------------------------
| 04 - Dimension Selectors
|--------------------------------------------------------------------------
*/

export const selectEvalDimensionsResult = (arg = {}) =>
    evalDimensionsApiSlice.endpoints
        .getEvalDimensions
        .select(arg)


export const makeSelectEvalDimensionsData = (arg = {}) =>
    createSelector(
        selectEvalDimensionsResult(arg),
        result => result.data
    )


export const makeEvalDimensionSelectors = (arg = {}) => {

    const selectData = makeSelectEvalDimensionsData(arg)

    return evalDimensionsAdapter.getSelectors(
        state => selectData(state) ?? initialState
    )

}


/*
|--------------------------------------------------------------------------
| 05 - Default Active Dimension Selectors
|--------------------------------------------------------------------------
*/

export const {

    selectAll: selectAllEvalDimensions,
    selectById: selectEvalDimensionById,
    selectIds: selectEvalDimensionIds,

} = makeEvalDimensionSelectors()